import React, { useState, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import DashboardTransition from "../components/DashboardTransition";
import {
  useDashboardStats,
  useTotalInteractions,
  useHumanInterventions,
  useDashboardInteractions,
} from "../hooks/useQueries";
import { LoadingSpinner } from "../components/ui/loading-spinner";
const Dashboard = () => {
  const { user } = useAuth();
  const targetDbName = user?.target_db_name;

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const monthLabel = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }, [selectedMonth]);

  const isCurrentMonth = useMemo(() => {
    const now = new Date();
    return selectedMonth === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, [selectedMonth]);

  const prevMonth = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const d = new Date(year, month - 2, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const nextMonth = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const d = new Date(year, month, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  // Usar React Query hooks para cache automático
  const { data: statsData, isLoading: statsLoading } =
    useDashboardStats(targetDbName);
  const { data: interactionsData, isLoading: interactionsLoading } =
    useTotalInteractions(targetDbName, selectedMonth);
  const { data: interventionsData, isLoading: interventionsLoading } =
    useHumanInterventions(targetDbName, selectedMonth);

  const { data: chartData, isLoading: chartLoading } =
    useDashboardInteractions(selectedMonth);

  // Estados derivados dos dados com React Query
  const stats = statsData?.stats || null;
  const totalInteractions = interactionsData?.total_interactions || 0;
  const humanInterventions = interventionsData?.total_interventions || 0;


  // Loading combinado
  const loading =
    statsLoading ||
    interactionsLoading ||
    interventionsLoading ||
    chartLoading;

  // Formatar dados do gráfico com useMemo para evitar recalculo
  const formattedChartData = useMemo(() => {
    return (chartData?.data || []).map((item) => ({
      dia: item.formatted_date,
      interacoes: item.interactions || 0,
    }));
  }, [chartData]);

  // Dados para o gráfico de pizza de interações
  const automaticInteractions = Math.max(
    0,
    totalInteractions - humanInterventions,
  );
  const validTotalInteractions = Math.max(
    totalInteractions,
    humanInterventions,
  );

  // Calcular automação % para o anel SVG
  const automationPct =
    validTotalInteractions > 0
      ? ((automaticInteractions / validTotalInteractions) * 100).toFixed(1)
      : "0.0";

  // SVG ring: circunferência = 2 * π * 84 ≈ 527.8
  const CIRCUMFERENCE = 2 * Math.PI * 84;
  const ringOffset = CIRCUMFERENCE - (parseFloat(automationPct) / 100) * CIRCUMFERENCE;

  // Normalizar barras do gráfico customizado para % da altura máxima
  const maxInteractions = useMemo(() => {
    if (!formattedChartData.length) return 1;
    return Math.max(...formattedChartData.map((d) => d.interacoes), 1);
  }, [formattedChartData]);

  const exportReport = () => {
    const totalContatos = (stats?.total_contatos || 0).toLocaleString('pt-BR');
    const totalInter = totalInteractions.toLocaleString('pt-BR');
    const totalIntervencoes = humanInterventions.toLocaleString('pt-BR');
    const autoAtual = automationPct;
    const geradoEm = new Date().toLocaleString('pt-BR');

    const diasRows = formattedChartData.length > 0
      ? formattedChartData.map(d => `
          <tr>
            <td>${d.dia}</td>
            <td>${d.interacoes.toLocaleString('pt-BR')}</td>
          </tr>`).join('')
      : '<tr><td colspan="2" style="text-align:center;color:#888">Sem dados no período</td></tr>';

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Relatório ${monthLabel}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family: 'Segoe UI', sans-serif; background:#fff; color:#111; padding:40px; }
    h1 { font-size:28px; font-weight:800; color:#0f1f0f; margin-bottom:4px; }
    .subtitle { font-size:14px; color:#555; margin-bottom:32px; }
    .meta { font-size:12px; color:#888; margin-bottom:32px; }
    .cards { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; margin-bottom:36px; }
    .card { border:1px solid #e0e0e0; border-radius:12px; padding:20px; background:#f9fafb; }
    .card-label { font-size:11px; text-transform:uppercase; letter-spacing:.08em; color:#666; margin-bottom:8px; }
    .card-value { font-size:28px; font-weight:800; color:#111; }
    .card-value.green { color:#16a34a; }
    h2 { font-size:16px; font-weight:700; margin-bottom:12px; color:#0f1f0f; }
    table { width:100%; border-collapse:collapse; font-size:13px; }
    thead tr { background:#f0fdf4; }
    th { text-align:left; padding:10px 14px; font-size:11px; text-transform:uppercase; letter-spacing:.06em; color:#555; border-bottom:2px solid #d1fae5; }
    td { padding:9px 14px; border-bottom:1px solid #f0f0f0; color:#222; }
    tr:hover td { background:#f9fafb; }
    .footer { margin-top:40px; font-size:11px; color:#aaa; text-align:center; }
    @media print { body { padding:20px; } button { display:none; } }
  </style>
</head>
<body>
  <h1>Relatório Mensal — <span style="text-transform:capitalize">${monthLabel}</span></h1>
  <p class="subtitle">Desempenho do motor de IA PapIA</p>
  <p class="meta">Gerado em: ${geradoEm}</p>
  <div class="cards">
    <div class="card">
      <div class="card-label">Total de Contatos</div>
      <div class="card-value">${totalContatos}</div>
    </div>
    <div class="card">
      <div class="card-label">Total de Interações</div>
      <div class="card-value green">${totalInter}</div>
    </div>
    <div class="card">
      <div class="card-label">Intervenções Humanas</div>
      <div class="card-value">${totalIntervencoes}</div>
    </div>
    <div class="card">
      <div class="card-label">Taxa de Automação</div>
      <div class="card-value green">${autoAtual}%</div>
    </div>
  </div>
  <h2>Interações por Dia</h2>
  <table>
    <thead><tr><th>Data</th><th>Interações</th></tr></thead>
    <tbody>${diasRows}</tbody>
  </table>
  <div class="footer">PapIA Dashboard · Relatório gerado automaticamente</div>
  <script>window.onload = () => window.print();</script>
</body>
</html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  };

  if (loading) {
    return (
      <DashboardTransition>
        <div className="flex items-center justify-center h-full min-h-[60vh]">
          <LoadingSpinner size="lg" />
        </div>
      </DashboardTransition>
    );
  }

  return (
    <DashboardTransition>
      <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 sm:space-y-8">

        {/* Hero Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-1">
            <h2 className="text-2xl sm:text-4xl font-extrabold font-headline tracking-tight text-on-surface">
              Visão Geral do Sistema
            </h2>
            <p className="text-on-surface-variant font-medium font-body">
              Desempenho em tempo real do motor de IA PapIA.
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <div className="flex items-center bg-surface-container-low border border-outline-variant/10 rounded-lg overflow-hidden">
              <button onClick={prevMonth} className="px-2 py-2 hover:bg-surface-container transition-colors text-on-surface-variant hover:text-on-surface">
                <span className="material-symbols-outlined text-lg leading-none">chevron_left</span>
              </button>
              <span className="px-3 py-2 text-sm font-semibold text-on-surface font-body min-w-[140px] text-center capitalize">
                {monthLabel}
              </span>
              <button onClick={nextMonth} disabled={isCurrentMonth} className="px-2 py-2 hover:bg-surface-container transition-colors text-on-surface-variant hover:text-on-surface disabled:opacity-30 disabled:cursor-not-allowed">
                <span className="material-symbols-outlined text-lg leading-none">chevron_right</span>
              </button>
            </div>
            <button onClick={exportReport} className="bg-surface-container-low text-on-surface-variant px-4 py-2 rounded-lg text-sm font-semibold border border-outline-variant/10 flex items-center gap-2 font-body hover:bg-surface-container transition-colors">
              <span className="material-symbols-outlined text-lg">download</span>
              Exportar Relatório
            </button>
          </div>
        </div>

        {/* Bento Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Stat Card 1 — Total de Contatos */}
          <div className="glass-card glow-emerald rounded-xl p-6 border border-outline-variant/10 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <span className="material-symbols-outlined text-6xl text-primary">groups</span>
            </div>
            <p className="text-on-surface-variant font-medium text-sm mb-2 font-body">Total de Contatos</p>
            <div className="flex items-baseline gap-3">
              <h3 className="text-3xl font-bold font-headline text-on-surface">
                {(stats?.total_contatos || 0).toLocaleString("pt-BR")}
              </h3>
            </div>
          </div>

          {/* Stat Card 2 — Total de Interações */}
          <div className="glass-card glow-emerald rounded-xl p-6 border border-outline-variant/10 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <span className="material-symbols-outlined text-6xl text-primary">hub</span>
            </div>
            <p className="text-on-surface-variant font-medium text-sm mb-2 font-body">Total de Interações</p>
            <div className="flex items-baseline gap-3">
              <h3 className="text-3xl font-bold font-headline text-on-surface">
                {totalInteractions.toLocaleString("pt-BR")}
              </h3>
            </div>
          </div>

          {/* Stat Card 3 — Intervenções Humanas */}
          <div className="glass-card glow-emerald rounded-xl p-6 border border-outline-variant/10 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <span className="material-symbols-outlined text-6xl text-error">support_agent</span>
            </div>
            <p className="text-on-surface-variant font-medium text-sm mb-2 font-body">Intervenções Humanas</p>
            <div className="flex items-baseline gap-3">
              <h3 className="text-3xl font-bold font-headline text-on-surface">
                {humanInterventions.toLocaleString("pt-BR")}
              </h3>
            </div>
          </div>

        </div>

        {/* Visual Data Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch pb-8">

          {/* Daily Interactions Bar Chart */}
          <div className="lg:col-span-8 glass-card glow-emerald rounded-xl p-4 sm:p-8 border border-outline-variant/10">
            <div className="flex justify-between items-center mb-10">
              <div>
                <h4 className="text-xl font-bold font-headline text-on-surface">Interações Diárias</h4>
                <p className="text-on-surface-variant text-xs font-body capitalize">Volume de tráfego em {monthLabel}</p>
              </div>
              <div className="flex gap-4 text-[10px] font-bold uppercase tracking-widest">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-primary rounded-full"></span>
                  <span className="text-on-surface-variant">Interações</span>
                </div>
              </div>
            </div>

            {formattedChartData.length > 0 ? (
              <>
                <div className="w-full overflow-hidden">
                  <div
                    className="flex items-end h-64 w-full"
                    style={{ gap: formattedChartData.length > 15 ? '2px' : '4px' }}
                  >
                    {formattedChartData.map((item, idx) => {
                      const pct = Math.round((item.interacoes / maxInteractions) * 100);
                      return (
                        <div
                          key={idx}
                          className="flex-1 min-w-0 h-full flex flex-col justify-end group"
                          title={`${item.dia}: ${item.interacoes}`}
                        >
                          <div className="relative w-full h-full flex flex-col justify-end">
                            <span className="text-center text-[9px] font-bold text-primary mb-1 opacity-0 group-hover:opacity-100 transition-opacity truncate">
                              {item.interacoes}
                            </span>
                            <div
                              className="w-full bg-primary rounded-t-sm transition-all group-hover:shadow-[0_0_15px_rgba(81,225,165,0.4)]"
                              style={{
                                height: `${Math.max(pct, item.interacoes > 0 ? 4 : 0)}%`,
                                minHeight: item.interacoes > 0 ? '6px' : '0',
                              }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex justify-between mt-4 text-[10px] text-on-surface-variant font-bold px-1 uppercase tracking-widest font-body">
                  {formattedChartData.length > 0 && (
                    <span>{formattedChartData[0]?.dia}</span>
                  )}
                  {formattedChartData.length > 1 && (
                    <span>{formattedChartData[Math.floor(formattedChartData.length / 2)]?.dia}</span>
                  )}
                  {formattedChartData.length > 2 && (
                    <span>{formattedChartData[formattedChartData.length - 1]?.dia}</span>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-64 text-on-surface-variant text-sm font-body">
                Nenhum dado disponível
              </div>
            )}
          </div>

          {/* Automation Health Ring */}
          <div className="lg:col-span-4 glass-card glow-emerald rounded-xl p-4 sm:p-8 border border-outline-variant/10 flex flex-col items-center justify-center text-center space-y-6">
            <div className="relative w-48 h-48 flex items-center justify-center">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 192 192">
                <circle
                  className="text-surface-container-highest"
                  cx="96"
                  cy="96"
                  fill="transparent"
                  r="84"
                  stroke="currentColor"
                  strokeWidth="12"
                />
                <circle
                  className="text-primary"
                  cx="96"
                  cy="96"
                  fill="transparent"
                  r="84"
                  stroke="currentColor"
                  strokeDasharray={CIRCUMFERENCE}
                  strokeDashoffset={ringOffset}
                  strokeLinecap="round"
                  strokeWidth="12"
                  style={{ filter: "drop-shadow(0 0 8px rgba(81,225,165,0.4))" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-extrabold font-headline text-on-surface">
                  {automationPct}%
                </span>
                <span className="text-[10px] uppercase tracking-widest text-on-surface-variant font-bold font-body">
                  Automatizado
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <h4 className="text-xl font-bold font-headline text-on-surface">Saúde da Automação</h4>
              <p className="text-sm text-on-surface-variant px-4 font-body">
                Seu agente de IA está lidando com{" "}
                {validTotalInteractions > 0
                  ? `${Math.round(parseFloat(automationPct) / 10)} de cada 10`
                  : "todas as"}{" "}
                solicitações sem assistência humana.
              </p>
            </div>
          </div>

        </div>
      </div>
    </DashboardTransition>
  );
};

export default Dashboard;
